import React from "react"
import { useStateMachine } from "../../../context/stateMachine/StateMachineV2"
import { Form } from "@carrotfertility/carotene-core-x"
import {
  useCheckRoutingInfoQueryCache,
  hasQuestionBeenSkipped,
  useUpdateRoutingInfo
} from "#/components/carrot-plans/shared/useQueryRoutingInfo"
import {
  RoutingInfoProviderPatchPayload,
  PROVIDER_CONSTANTS
} from "#/components/carrot-plans/utils/providerSelectionUtils"
import {
  useMemberProviderSelectionQuery,
  useMemberProviderSelectionMutation
} from "../../hooks/useMemberProviderSelection"
import { MemberProviderSelection, ProviderSelectionAction } from "#/types/memberProviderSelection"
import { FormattedMessage, useIntl, defineMessages, defineMessage } from "react-intl"
import {
  ButtonFooterLayout,
  ContinueButton,
  SkipButton,
  RoutingFlowTitle,
  StepLayout,
  useConvertTranslatedLabel
} from "#/components/carrot-plans/shared"
import { Stack, useTheme } from "@carrotfertility/carotene-core"
import { FormRadioButtonGroup } from "#/services/common-forms"
import { CenteredLoadingIndicator } from "../../../views/molecules/Molecules"
import { JourneyStage } from "#/components/carrot-plans/types/journeyStage"
import { ProviderType } from "#/pages/provider-finder/utils/parentIdMapping"

const providerTypeLabels = defineMessages({
  FERTILITY_CLINIC: { defaultMessage: "A fertility clinic" },
  OBGYN: { defaultMessage: "An OBGYN" },
  NONE: { defaultMessage: "I don't have a provider" }
})

interface ProviderTypeStepProps {
  title: string
  options: Array<{ value: string; label: ReturnType<typeof defineMessage> }>
  defaultValue?: string
  routingInfoId?: number
  /**
   * Saved MemberProviderSelection data passed in from the parent component.
   * The parent already fetches this for defaultValue/loading, so we accept it as a prop
   * to avoid calling useMemberProviderSelectionQuery a second time in this child.
   */
  existingProviderSelection: MemberProviderSelection | null | undefined
  getAction: (providerType: string | undefined) => ProviderSelectionAction
  /**
   * A stable key derived from the current query data by the parent. When the
   * background refetch after a journey/phase change returns null (clearing the
   * server-side selection), the key changes and React remounts the Form so
   * React Hook Form picks up the fresh empty defaultValues. Without this, RHF
   * freezes defaultValues at mount time and ignores subsequent prop changes.
   */
}

function ProviderTypeStep({
  title,
  options,
  defaultValue,
  routingInfoId,
  existingProviderSelection,
  getAction
}: ProviderTypeStepProps): JSX.Element {
  const theme = useTheme()
  const routingData = useCheckRoutingInfoQueryCache()
  const memberProviderSelectionMutation = useMemberProviderSelectionMutation()
  const { mutateAsync: updateRoutingInfoAsync } = useUpdateRoutingInfo()

  const updateRoutingInfo = (payload: RoutingInfoProviderPatchPayload) => updateRoutingInfoAsync(payload)

  // existingProviderSelection is passed in from the parent (which already fetched it for defaultValue),
  // so no second query is needed here.
  const wasSkipped = hasQuestionBeenSkipped("whatKindOfProvider", routingData)

  function getProviderTypeSendValue(): string | null {
    if (wasSkipped) return "SKIP_CLINIC_NAME"
    if (existingProviderSelection?.providerType === ProviderType.FertilityClinics) return "FERTILITY_CLINIC"
    if (existingProviderSelection?.providerType === PROVIDER_CONSTANTS.OBGYN_PROVIDER_TYPE) return "OBGYN"
    if (existingProviderSelection?.providerSelectionAction === ProviderSelectionAction.NO_PROVIDER)
      return "SKIP_CLINIC_NAME"
    return null
  }

  const { send } = useStateMachine(getProviderTypeSendValue())

  const radioOptions = useConvertTranslatedLabel(options).map(({ label, value }) => ({
    label,
    value
  }))

  async function onSkip(): Promise<void> {
    // Always write to MemberProviderSelection on skip, even on first visit when no prior row exists.
    // This ensures a row is created with SKIPPED action so that re-entry auto-advance works
    // correctly. If the row already exists (e.g. user previously answered and is now going back),
    // this clears the stale providerType/action data.
    await memberProviderSelectionMutation.mutateAsync({
      selectionSource: "ROUTING",
      providerType: null,
      providerSelectionAction: ProviderSelectionAction.SKIPPED,
      routingInfoId: routingInfoId,
      sawProviderTypeStep: true
    })
    // Record the skip for this step. Also clear any stale nameOfClinic/nameOfObGyn skip records —
    // those steps are never shown when this step is skipped, so any prior skip is no longer valid.
    await updateRoutingInfo({
      skipFields: ["whatKindOfProvider"],
      clearProviderSkipFields: ["nameOfClinic", "nameOfObGyn"]
    })
    send("SKIP_CLINIC_NAME")
  }

  async function onClickContinue(formValues: { providerType?: string }): Promise<void> {
    if (!formValues.providerType) {
      await onSkip()
      return
    }

    const action = getAction(formValues.providerType)
    const providerType = formValues.providerType === "NO_PROVIDER" ? null : formValues.providerType

    // Write provider selection data and signal skip record removal if previously skipped.
    // Also clears any stale nameOfClinic/nameOfObGyn skip records so the downstream name step
    // is presented fresh (not auto-skipped based on a prior pass through the flow).
    //
    // When provider type hasn't changed (e.g. user went back and clicked Continue), preserve
    // the existing name data for that type so we don't clear it. If the type DID change, omit
    // name fields so the backend clears them and the downstream name step collects a fresh value.
    const providerTypeUnchanged = providerType !== null && providerType === existingProviderSelection?.providerType
    const preservedProviderSelectionData =
      providerTypeUnchanged &&
      (providerType === PROVIDER_CONSTANTS.OBGYN_PROVIDER_TYPE
        ? {
            providerFirstName: existingProviderSelection?.providerFirstName,
            providerLastName: existingProviderSelection?.providerLastName
          }
        : {
            providerEntityName: existingProviderSelection?.providerEntityName,
            providerUuid: existingProviderSelection?.providerUuid,
            providerEligibilityStatusAtSelection: existingProviderSelection?.providerEligibilityStatusAtSelection
          })
    await memberProviderSelectionMutation.mutateAsync({
      selectionSource: "ROUTING",
      providerType: providerType,
      providerSelectionAction: action,
      routingInfoId: routingInfoId,
      sawProviderTypeStep: true,
      ...(preservedProviderSelectionData || {})
    })

    await updateRoutingInfo({
      clearProviderSkipFields: wasSkipped
        ? ["whatKindOfProvider", "nameOfClinic", "nameOfObGyn"]
        : ["nameOfClinic", "nameOfObGyn"]
    })

    if (formValues.providerType === ProviderType.FertilityClinics) {
      send("FERTILITY_CLINIC")
    } else if (formValues.providerType === PROVIDER_CONSTANTS.OBGYN_PROVIDER_TYPE) {
      send("OBGYN")
    } else {
      send("SKIP_CLINIC_NAME")
    }
  }

  return (
    <>
      <RoutingFlowTitle title={title} />
      <StepLayout
        header={<span id="provider-type-label">{title}</span>}
        formElements={
          <Form defaultValues={{ providerType: defaultValue }} onSubmit={onClickContinue}>
            <Stack spacing={theme.spacing(theme.tokens.spacing.sm)}>
              <FormRadioButtonGroup name="providerType" options={radioOptions} aria-labelledby="provider-type-label" />
            </Stack>
            <ButtonFooterLayout
              continueButton={
                <ContinueButton>
                  <FormattedMessage defaultMessage="Continue" />
                </ContinueButton>
              }
              skipButton={<SkipButton onClick={onSkip} />}
            />
          </Form>
        }
      />
    </>
  )
}

function WhatKindOfProviderForIuiStep(): JSX.Element {
  const routingData = useCheckRoutingInfoQueryCache()
  const routingInfoId = routingData?.routingInfoId
  const journeyStage = routingData?.journeyStage
  const memberProviderSelectionQuery = useMemberProviderSelectionQuery(routingInfoId)
  const intl = useIntl()

  if (memberProviderSelectionQuery.isLoading || memberProviderSelectionQuery.isFetching) {
    return <CenteredLoadingIndicator />
  }

  const isRecentlyCompleted = journeyStage === JourneyStage.RECENTLY_COMPLETED_IUI_CYCLE
  const title = isRecentlyCompleted
    ? intl.formatMessage({ defaultMessage: "What kind of provider did you see for IUI?" })
    : intl.formatMessage({ defaultMessage: "What kind of provider are you seeing for IUI?" })

  const defaultValue = memberProviderSelectionQuery.data?.providerType

  const options = [
    { value: ProviderType.FertilityClinics, label: providerTypeLabels.FERTILITY_CLINIC },
    { value: PROVIDER_CONSTANTS.OBGYN_PROVIDER_TYPE, label: providerTypeLabels.OBGYN }
  ]

  // IUI always implies HAS_PROVIDER — there's no "no provider" option for this variant.
  // The providerType argument is intentionally ignored; the action is always the same.
  const getIuiAction = (_providerType?: string) => ProviderSelectionAction.HAS_PROVIDER

  return (
    <ProviderTypeStep
      title={title}
      options={options}
      defaultValue={defaultValue}
      routingInfoId={routingInfoId}
      existingProviderSelection={memberProviderSelectionQuery.data}
      getAction={getIuiAction}
    />
  )
}

function WhatKindOfProviderGeneralStep(): JSX.Element {
  const routingData = useCheckRoutingInfoQueryCache()
  const routingInfoId = routingData?.routingInfoId
  const journeyStageValue = routingData?.journeyStage
  const memberProviderSelectionQuery = useMemberProviderSelectionQuery(routingInfoId)
  const intl = useIntl()

  if (memberProviderSelectionQuery.isLoading || memberProviderSelectionQuery.isFetching) {
    return <CenteredLoadingIndicator />
  }

  const isGettingReadyStartIui = journeyStageValue === JourneyStage.GETTING_READY_START_IUI
  const title = isGettingReadyStartIui
    ? intl.formatMessage({
        defaultMessage: "If you have a provider, what kind of provider will you be seeing for IUI?"
      })
    : intl.formatMessage({ defaultMessage: "If you have a provider, what kind of provider will you be seeing?" })

  const existingProviderSelection = memberProviderSelectionQuery.data
  const defaultValue =
    existingProviderSelection?.providerType ||
    (existingProviderSelection?.providerSelectionAction === ProviderSelectionAction.NO_PROVIDER
      ? "NO_PROVIDER"
      : undefined)

  const options = [
    { value: ProviderType.FertilityClinics, label: providerTypeLabels.FERTILITY_CLINIC },
    { value: PROVIDER_CONSTANTS.OBGYN_PROVIDER_TYPE, label: providerTypeLabels.OBGYN },
    { value: "NO_PROVIDER", label: providerTypeLabels.NONE }
  ]

  function getAction(providerType: string | undefined): ProviderSelectionAction {
    return providerType === "NO_PROVIDER" ? ProviderSelectionAction.NO_PROVIDER : ProviderSelectionAction.HAS_PROVIDER
  }

  return (
    <ProviderTypeStep
      title={title}
      options={options}
      defaultValue={defaultValue}
      routingInfoId={routingInfoId}
      existingProviderSelection={memberProviderSelectionQuery.data}
      getAction={getAction}
    />
  )
}

export { WhatKindOfProviderGeneralStep, WhatKindOfProviderForIuiStep }
